import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, Ban, CalendarClock, Target, Users, Video } from 'lucide-react';
import { LessonParticipantRole, LessonStatus } from '@tutor/shared';
import {
  Alert,
  Avatar,
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  EmptyState,
  ErrorState,
  LoadingState,
  Modal,
  PageHeader,
  Textarea,
} from '@tutor/ui';
import { ApiError } from '../../lib/api-client';
import { useCancelLesson, useLesson } from '../../hooks/queries';
import { formatDateTime, formatDuration, formatRelativeDayTime, humaniseEnum } from '../../lib/format';

export function LessonDetailPage({ perspective }: { perspective: 'tutor' | 'student' }) {
  const { lessonId } = useParams<{ lessonId: string }>();
  const { data: lesson, isLoading, error } = useLesson(lessonId);
  const basePath = perspective === 'tutor' ? '/tutor' : '/student';

  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [cancelError, setCancelError] = useState<string | null>(null);
  const cancelLesson = useCancelLesson(lessonId ?? '');

  const onCancel = async (): Promise<void> => {
    setCancelError(null);
    try {
      await cancelLesson.mutateAsync(cancelReason.trim() || undefined);
      setCancelOpen(false);
      setCancelReason('');
    } catch (err) {
      setCancelError(err instanceof ApiError ? err.message : 'We could not cancel that lesson.');
    }
  };

  if (isLoading) return <LoadingState />;

  if (error) {
    const apiError = error instanceof ApiError ? error : null;
    return (
      <ErrorState
        title={apiError?.isForbidden ? 'You do not have access to this lesson' : 'Lesson not found'}
        description={apiError?.message}
        action={
          <Link to={`${basePath}/lessons`}>
            <Button variant="outline">Back to lessons</Button>
          </Link>
        }
      />
    );
  }

  if (!lesson) return null;

  return (
    <>
      <Link
        to={`${basePath}/lessons`}
        className="mb-4 inline-flex items-center gap-1.5 text-sm font-medium text-slate-500 hover:text-slate-700"
      >
        <ArrowLeft className="h-4 w-4" aria-hidden />
        All lessons
      </Link>

      <PageHeader
        title={lesson.title}
        description={
          <span className="flex flex-wrap items-center gap-2">
            <span>{formatRelativeDayTime(lesson.scheduledStart)}</span>
            <span aria-hidden>·</span>
            <span>{formatDuration(lesson.scheduledStart, lesson.scheduledEnd)}</span>
            {lesson.subject && <Badge tone="brand">{lesson.subject.name}</Badge>}
            <Badge tone={lesson.status === LessonStatus.COMPLETED ? 'info' : 'neutral'}>
              {humaniseEnum(lesson.status)}
            </Badge>
          </span>
        }
        action={
          <div className="flex items-center gap-2">
            {perspective === 'tutor' &&
              lesson.status !== LessonStatus.CANCELLED &&
              lesson.status !== LessonStatus.COMPLETED && (
                <Button
                  variant="outline"
                  onClick={() => setCancelOpen(true)}
                  leftIcon={<Ban className="h-4 w-4" aria-hidden />}
                >
                  Cancel lesson
                </Button>
              )}
            {lesson.canJoin && (
              <Link to={`${basePath}/classroom/${lesson.id}`}>
                <Button leftIcon={<Video className="h-4 w-4" aria-hidden />}>
                  {perspective === 'tutor' ? 'Enter Classroom' : 'Join Classroom'}
                </Button>
              </Link>
            )}
          </div>
        }
      />

      <Modal
        open={cancelOpen}
        onClose={() => setCancelOpen(false)}
        title="Cancel this lesson?"
        description="Your student will be notified straight away."
        size="sm"
        footer={
          <>
            <Button variant="outline" onClick={() => setCancelOpen(false)}>
              Keep lesson
            </Button>
            <Button variant="danger" onClick={() => void onCancel()} isLoading={cancelLesson.isPending}>
              Cancel lesson
            </Button>
          </>
        }
      >
        <div className="space-y-3">
          {cancelError && <Alert tone="danger">{cancelError}</Alert>}
          <Textarea
            label="Reason"
            hint="Optional. Shared with the student."
            rows={3}
            value={cancelReason}
            onChange={(event) => setCancelReason(event.target.value)}
            placeholder="Unwell — I will rebook this week."
          />
        </div>
      </Modal>

      <div className="grid gap-6 lg:grid-cols-3">
        <div className="space-y-6 lg:col-span-2">
          <Card>
            <CardHeader title="About this lesson" />
            <CardBody className="space-y-4">
              <p className="text-sm text-slate-700">
                {lesson.description ?? 'No description was added for this lesson.'}
              </p>

              {lesson.topic && (
                <div>
                  <p className="text-xs font-medium uppercase tracking-wide text-slate-500">Topic</p>
                  <p className="mt-0.5 text-sm text-slate-900">
                    {lesson.topic.name}
                    <span className="text-slate-500"> · {lesson.topic.courseName}</span>
                  </p>
                </div>
              )}

              {lesson.objectives.length > 0 && (
                <div>
                  <p className="mb-1.5 flex items-center gap-1.5 text-xs font-medium uppercase tracking-wide text-slate-500">
                    <Target className="h-3.5 w-3.5" aria-hidden />
                    Objectives
                  </p>
                  <ul className="space-y-1">
                    {lesson.objectives.map((objective) => (
                      <li key={objective} className="flex gap-2 text-sm text-slate-700">
                        <span className="mt-2 h-1 w-1 shrink-0 rounded-full bg-slate-400" aria-hidden />
                        {objective}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </CardBody>
          </Card>

          <Card>
            <CardHeader title="Lesson notes" description="Notes marked private are only visible to the tutor." />
            <CardBody>
              {lesson.notes.length === 0 ? (
                <EmptyState title="No notes yet" />
              ) : (
                <ul className="space-y-3">
                  {lesson.notes.map((note) => (
                    <li key={note.id} className="rounded-lg border border-slate-100 p-3">
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-medium text-slate-900">{note.authorName}</p>
                        {note.isPrivate && <Badge tone="warning">Private</Badge>}
                        <span className="ml-auto text-xs text-slate-400">{formatDateTime(note.createdAt)}</span>
                      </div>
                      <p className="mt-1.5 whitespace-pre-wrap text-sm text-slate-700">{note.body}</p>
                    </li>
                  ))}
                </ul>
              )}
            </CardBody>
          </Card>
        </div>

        <div className="space-y-6">
          <Card>
            <CardHeader title="Participants" />
            <CardBody>
              <ul className="space-y-3">
                {lesson.participants.map((participant) => (
                  <li key={participant.id} className="flex items-center gap-3">
                    <Avatar name={participant.user.fullName} src={participant.user.avatarUrl} size="sm" />
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-medium text-slate-900">{participant.user.fullName}</p>
                      <p className="text-xs text-slate-500">
                        {participant.participantRole === LessonParticipantRole.HOST ? 'Tutor' : 'Student'}
                        {participant.attendance && ` · ${humaniseEnum(participant.attendance)}`}
                      </p>
                    </div>
                  </li>
                ))}
              </ul>
            </CardBody>
          </Card>

          <Card>
            <CardHeader title="Schedule" />
            <CardBody className="space-y-2 text-sm">
              <div className="flex items-center gap-2 text-slate-700">
                <CalendarClock className="h-4 w-4 text-slate-400" aria-hidden />
                {formatDateTime(lesson.scheduledStart)}
              </div>
              <div className="flex items-center gap-2 text-slate-700">
                <Users className="h-4 w-4 text-slate-400" aria-hidden />
                {lesson.students.length} student{lesson.students.length === 1 ? '' : 's'}
              </div>
              {lesson.actualStart && (
                <p className="text-xs text-slate-500">Started {formatDateTime(lesson.actualStart)}</p>
              )}
              {lesson.actualEnd && (
                <p className="text-xs text-slate-500">Ended {formatDateTime(lesson.actualEnd)}</p>
              )}
            </CardBody>
          </Card>
        </div>
      </div>
    </>
  );
}
